import { Alert, Button } from "@amzn/awsui-components-console";
import React, { useMemo } from "react";
import { CSVLink } from "react-csv";

import { buildCsvExport } from "./exportBuilder";
import type { CsvExport } from "./exportBuilder";
import type { ClaimRecord, ExportConfig } from "./types";

export interface ConfigDrivenExportButtonProps {
  readonly exportConfig: ExportConfig;
  /**
   * The full retrieved result set, in retrieval order, which is the order the
   * file is written in.
   *
   * Deliberately not what the table currently displays: `SearchResults` sorts,
   * filters, and paginates internally through `useCollection`, and none of that
   * reaches here. So a file can contain rows the text filter has hidden and
   * order them differently from the visible table. The three legacy components
   * pass their retrieved list to both the table and the export the same way, so
   * matching them keeps the exported file identical across the flag.
   */
  readonly claims: readonly ClaimRecord[];
}

type BuildAttempt = { readonly ok: true; readonly csv: CsvExport } | { readonly ok: false };

const EXPORT_LABEL = "Export to CSV";

/**
 * The results-header export control.
 *
 * `buildCsvExport` throws rather than writing an incomplete file, so the build
 * is contained here: a config that cannot produce a complete export costs the
 * user the export and nothing else. The rows and the search state are never
 * touched either way, since this component holds neither.
 */
function ConfigDrivenExportButton({
  exportConfig,
  claims,
}: ConfigDrivenExportButtonProps): React.ReactElement | null {
  const attempt = useMemo<BuildAttempt>(() => {
    try {
      return { ok: true, csv: buildCsvExport(exportConfig, claims) };
    } catch (error) {
      // Reported to the user below. Logged as well because the cause is a
      // configuration defect an engineer has to read to fix.
      console.error(error);
      return { ok: false };
    }
  }, [exportConfig, claims]);

  // An export of nothing is not offered at all, rather than offered and empty.
  if (claims.length === 0) {
    return null;
  }

  if (!attempt.ok) {
    // No control alongside the alert. Every throw `buildCsvExport` raises is a
    // defect in the declared export config, so a rebuild against the same config
    // fails the same way — a retry would look actionable and never succeed. A
    // change to the config or the claims rebuilds through the memo anyway, so
    // recovery does not need a button.
    return (
      <Alert type="error" statusIconAriaLabel="Error" data-testid="export-failure-alert">
        The export is unavailable for this view. The claims shown are unaffected.
      </Alert>
    );
  }

  return (
    <CSVLink
      data={[...attempt.csv.rows]}
      headers={[...attempt.csv.headers]}
      filename={attempt.csv.fileName}
      data-testid="export-csv-link"
    >
      <Button variant="normal" data-testid="export-csv-button">
        {EXPORT_LABEL}
      </Button>
    </CSVLink>
  );
}

export default ConfigDrivenExportButton;
